var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
let HEADER_THRESHOLD = 200;
class ScrollDispatcher {
  constructor() {
    __publicField(this, "scrollY", 0);
    __publicField(this, "prevScrollY", 0);
    __publicField(this, "dir", null);
    __publicField(this, "prevDir", null);
    __publicField(this, "passed", false);
    __publicField(this, "_lenis", null);
    __publicField(this, "_lastTime", 0);
    __publicField(this, "_THROTTLE_MS", 100);
    // ~10fps — sufficient for direction & threshold detection
    __publicField(this, "_NATIVE_IDLE_FRAME_LIMIT", 8);
    __publicField(this, "_subs", {
      update: /* @__PURE__ */ new Set(),
      "frame-update": /* @__PURE__ */ new Set(),
      "direction-change": /* @__PURE__ */ new Set(),
      "passed-header": /* @__PURE__ */ new Set()
    });
    __publicField(this, "_headerThreshold", 200);
    __publicField(this, "_frameScrollY", 0);
    __publicField(this, "_frameDir", null);
    __publicField(this, "_nativeFramePredicates", /* @__PURE__ */ new Map());
    __publicField(this, "_nativeFrameRaf", 0);
    __publicField(this, "_nativeIdleFrames", 0);
    __publicField(this, "_lockCount", 0);
    __publicField(this, "_lockTimeout", null);
    __publicField(this, "_lockScrollY", 0);
    __publicField(this, "_onNative", () => {
      if (this._lockCount > 0) return;
      const y = this._y();
      this._publishFrameUpdate(y);
      if (this._hasActiveNativeFramePredicate(y)) {
        this._startNativeFrameLoop();
      }
      const now = performance.now();
      if (now - this._lastTime < this._THROTTLE_MS) {
        if (!this._trailingRaf) {
          this._trailingRaf = requestAnimationFrame(() => {
            this._trailingRaf = 0;
            this._lastTime = performance.now();
            this._onScroll();
          });
        }
        return;
      }
      if (this._trailingRaf) {
        cancelAnimationFrame(this._trailingRaf);
        this._trailingRaf = 0;
      }
      this._lastTime = now;
      this._onScroll({ scroll: y });
    });
    __publicField(this, "_onNativeScrollEnd", () => {
      if (this._lockCount > 0 || this._lenis) return;
      this._stopNativeFrameLoop();
      this._publishFrameUpdate(window.scrollY ?? 0);
    });
    __publicField(this, "_onVisibilityChange", () => {
      if (document.hidden) this._stopNativeFrameLoop();
    });
    __publicField(this, "_onScroll", (e) => {
      if (this._lockCount > 0) return;
      const y = (e == null ? void 0 : e.scroll) ?? this._y();
      if (this._lenis) this._publishFrameUpdate(y, e == null ? void 0 : e.direction);
      const dy = y - this.scrollY;
      if (Math.abs(dy) < 1 && y !== 0) return;
      let dir = this.dir;
      if (this._lenis && (e == null ? void 0 : e.direction)) {
        dir = e.direction === 1 ? "down" : e.direction === -1 ? "up" : this.dir;
      } else {
        dir = dy > 0 ? "down" : "up";
      }
      const dirChanged = dir !== this.prevDir && this.prevDir !== null;
      const wasPassed = this.passed;
      const passed = y > this._headerThreshold;
      this.prevScrollY = this.scrollY;
      this.prevDir = this.dir;
      this.scrollY = y;
      this.dir = dir;
      this.passed = passed;
      const d = { scrollY: y, direction: dir, isPassedHeader: passed, passed };
      this._fire("update", d);
      if (dirChanged) this._fire("direction-change", d);
      if (passed !== wasPassed) this._fire("passed-header", d);
    });
  }
  init() {
    this._bound = false;
    window.addEventListener("scroll-dispatcher:set-threshold", (e) => this.setThreshold(e.detail));
    window.dispatchEvent(new CustomEvent("scroll-dispatcher:ready", { detail: { dispatcher: this } }));
    return this;
  }
  setThreshold(val) {
    this._headerThreshold = val;
    HEADER_THRESHOLD = val;
    const wasPassed = this.passed;
    this.passed = this.scrollY > this._headerThreshold;
    if (this.passed !== wasPassed) {
      this._fire("passed-header", this.getState());
    }
  }
  /** Bind scroll listener lazily — only when something actually subscribes. */
  _ensureBound() {
    if (this._bound) return;
    this._bound = true;
    this.scrollY = this.prevScrollY = this._y();
    this._frameScrollY = this.scrollY;
    this.passed = this.scrollY > this._headerThreshold;
    this._bind();
  }
  setLenis(lenis) {
    if (this._lenis) this._lenis.off("scroll", this._onScroll);
    this._stopNativeFrameLoop();
    this._lenis = lenis;
    this._bind();
  }
  on(type, cb, options) {
    var _a;
    this._ensureBound();
    (_a = this._subs[type]) == null ? void 0 : _a.add(cb);
    if (type === "frame-update" && typeof (options == null ? void 0 : options.shouldPollNative) === "function") {
      this._nativeFramePredicates.set(cb, options.shouldPollNative);
    }
    return () => {
      var _a2;
      (_a2 = this._subs[type]) == null ? void 0 : _a2.delete(cb);
      this._nativeFramePredicates.delete(cb);
      if (!this._hasActiveNativeFramePredicate(this._frameScrollY)) {
        this._stopNativeFrameLoop();
      }
    };
  }
  getState() {
    return { scrollY: this.scrollY, direction: this.dir, isPassedHeader: this.passed, isLocked: this._lockCount > 0 };
  }
  // --- Lock / Unlock ---
  lock(delayMs = 0) {
    var _a;
    if ((_a = window.Shopify) == null ? void 0 : _a.designMode) return;
    this._lockCount++;
    if (this._lockCount === 1) {
      this._stopNativeFrameLoop();
      if (this._lenis) {
        this._lenis.stop();
      }
      const isTouch = window.matchMedia("(pointer: coarse)").matches;
      if (!this._lenis && !isTouch) {
        this._lockScrollY = window.scrollY;
        Object.assign(document.body.style, {
          position: "fixed",
          top: `-${this._lockScrollY}px`,
          width: "100%",
          left: "0"
        });
        this._usingBodyFix = true;
      } else if (!this._lenis && isTouch) {
        this._lockScrollY = window.scrollY;
        this._usingBodyFix = false;
      }
      this._lockTimeout = setTimeout(() => {
        document.documentElement.setAttribute("no-scroll", "");
      }, delayMs);
      window.dispatchEvent(new CustomEvent("scroll-dispatcher:locked"));
    }
  }
  unlock() {
    var _a;
    if ((_a = window.Shopify) == null ? void 0 : _a.designMode) return;
    this._lockCount = Math.max(0, this._lockCount - 1);
    if (this._lockCount === 0) {
      clearTimeout(this._lockTimeout);
      document.documentElement.removeAttribute("no-scroll");
      if (this._usingBodyFix) {
        Object.assign(document.body.style, { position: "", top: "", width: "", left: "" });
        window.scrollTo({ left: 0, top: this._lockScrollY, behavior: "instant" });
        this._usingBodyFix = false;
      } else if (!this._lenis && this._lockScrollY !== void 0) {
        this._lockScrollY = void 0;
      }
      if (this._lenis) {
        this._lenis.start();
      }
      window.dispatchEvent(new CustomEvent("scroll-dispatcher:unlocked"));
    }
  }
  // --- Internal ---
  _y() {
    var _a;
    return ((_a = this._lenis) == null ? void 0 : _a.scroll) ?? window.scrollY ?? 0;
  }
  _bind() {
    if (this._lenis) this._lenis.off("scroll", this._onScroll);
    window.removeEventListener("scroll", this._onNative);
    window.removeEventListener("scrollend", this._onNativeScrollEnd);
    document.removeEventListener("visibilitychange", this._onVisibilityChange);
    document.addEventListener("visibilitychange", this._onVisibilityChange);
    if (this._lenis) {
      this._lenis.on("scroll", this._onScroll);
    } else {
      window.addEventListener("scroll", this._onNative, { passive: true });
      window.addEventListener("scrollend", this._onNativeScrollEnd, { passive: true });
    }
  }
  /** Fill gaps between native scroll events only for opted-in visual consumers. */
  _startNativeFrameLoop() {
    if (this._lenis || document.hidden) return;
    this._nativeIdleFrames = 0;
    if (this._nativeFrameRaf) return;
    const tick = () => {
      this._nativeFrameRaf = 0;
      if (this._lockCount > 0 || this._lenis || document.hidden) return;
      const y = window.scrollY ?? 0;
      const changed = Math.abs(y - this._frameScrollY) >= 1e-3;
      if (changed) {
        this._nativeIdleFrames = 0;
        this._publishFrameUpdate(y);
      } else {
        this._nativeIdleFrames += 1;
      }
      if (this._nativeIdleFrames >= this._NATIVE_IDLE_FRAME_LIMIT || !this._hasActiveNativeFramePredicate(y)) {
        return;
      }
      this._nativeFrameRaf = requestAnimationFrame(tick);
    };
    this._nativeFrameRaf = requestAnimationFrame(tick);
  }
  _stopNativeFrameLoop() {
    if (this._nativeFrameRaf) cancelAnimationFrame(this._nativeFrameRaf);
    this._nativeFrameRaf = 0;
    this._nativeIdleFrames = 0;
  }
  _hasActiveNativeFramePredicate(y) {
    if (!this._nativeFramePredicates.size) return false;
    for (const predicate of this._nativeFramePredicates.values()) {
      if (predicate(y)) return true;
    }
    return false;
  }
  _fire(type, d) {
    var _a;
    (_a = this._subs[type]) == null ? void 0 : _a.forEach((cb) => cb(d));
  }
  /** Publish compositor-facing scroll state without touching throttled state. */
  _publishFrameUpdate(y, lenisDirection) {
    const dy = y - this._frameScrollY;
    if (Math.abs(dy) < 1e-3) return;
    let direction = this._frameDir;
    if (lenisDirection === 1) {
      direction = "down";
    } else if (lenisDirection === -1) {
      direction = "up";
    } else if (dy !== 0) {
      direction = dy > 0 ? "down" : "up";
    }
    this._frameScrollY = y;
    this._frameDir = direction;
    const passed = y > this._headerThreshold;
    this._fire("frame-update", {
      scrollY: y,
      direction,
      isPassedHeader: passed,
      passed
    });
  }
  destroy() {
    if (this._lenis) this._lenis.off("scroll", this._onScroll);
    window.removeEventListener("scroll", this._onNative);
    window.removeEventListener("scrollend", this._onNativeScrollEnd);
    document.removeEventListener("visibilitychange", this._onVisibilityChange);
    this._stopNativeFrameLoop();
    if (this._trailingRaf) cancelAnimationFrame(this._trailingRaf);
    this._nativeFramePredicates.clear();
    Object.values(this._subs).forEach((s) => s.clear());
  }
}
window.__SCROLL_DISPATCHER__ = window.__SCROLL_DISPATCHER__ || new ScrollDispatcher();
const scrollDispatcher = window.__SCROLL_DISPATCHER__;
const onScrollDirectionChange = (cb) => scrollDispatcher.on("direction-change", cb);
const onScrollUpdate = (cb) => scrollDispatcher.on("update", cb);
const onScrollFrameUpdate = (cb, options) => scrollDispatcher.on("frame-update", cb, options);
const onPassedHeader = (cb) => scrollDispatcher.on("passed-header", cb);
const getScrollState = () => scrollDispatcher.getState();
const lockScroll = (delayMs = 0) => scrollDispatcher.lock(delayMs);
const unlockScroll = () => scrollDispatcher.unlock();
if (!window.__SCROLL_DISPATCHER_INIT__) {
  window.__SCROLL_DISPATCHER_INIT__ = true;
  scrollDispatcher.init();
  window.Theme = window.Theme || {};
  window.Theme.scrollDispatcher = scrollDispatcher;
  window.Theme.onScrollDirectionChange = onScrollDirectionChange;
  window.Theme.onScrollUpdate = onScrollUpdate;
  window.Theme.onScrollFrameUpdate = onScrollFrameUpdate;
  window.Theme.onPassedHeader = onPassedHeader;
  window.Theme.getScrollState = getScrollState;
  window.Theme.getScrollDirection = () => scrollDispatcher.getState().direction;
}
export {
  HEADER_THRESHOLD,
  scrollDispatcher as default,
  getScrollState,
  lockScroll,
  onPassedHeader,
  onScrollDirectionChange,
  onScrollFrameUpdate,
  onScrollUpdate,
  scrollDispatcher,
  unlockScroll
};
